import { resolveDocumentCollectionElement } from "@needle-tools/addressing/document";
import type { PropertyDiscovery } from "../../properties/common/propertyDiscovery.js";

import { PropertyOwnerDefinition } from "../../properties/common/propertyOwnerDefinition.js";
import { methodReadDefinition } from "../../properties/common/propertyReadDefinition.js";

import { BufferGeometry, type IUniform, Material, Object3D, Texture, Uniform, UniformsLib, WebGLRenderer } from "three";
import { isProxyInvariantError } from "../../../base/common/errors.js";
import type { ILogService } from "../../log/common/log.js";
import { type IProperty, type IPropertyCollection, type IPropertyProvider, PropertyUtils, Property, type PropertyAddress, type IPropertyGroup, PropertyCollection, PropertyGroup, type IPropertyStateProvider, type GetPropertyOptions, PropertyViewMode, notifyPropertyChildrenMaterialized, type PropertyChangedEvent, type PropertyMetadata } from "../../properties/common/properties.js";
import { isAnimationClipLike, isAnimationMixer, isCamera, isGeometry, isLikelyMaterialTextureInputSlot, isLoadedModelResult, isMaterial, isObject3D, isObjectWithMaterials, isPerspectiveCamera, isRenderer, isScene, isTexture, isTSLNode } from "./helper.js";
import { isModelProjection } from "../../resources/common/modelProjection.js";
import { isColorLike, isMatrixLike, isTextureLike, isVectorPropertyLike } from "../../properties/common/types.js";
import type { ThreeJs } from "./webgl.js";

import type { IPropertyAccessorService } from "../../properties/common/accessor.js";
import { AnimationTimeAccessor, ColorPropertyAccessor, MatrixPropertyAccessor, ObjectReferenceAccessor, TexturePropertyAccessor, ThreeObjectMetadataAccessor, VectorPropertyAccessor, TSLInputNodeAccessor } from "./accessor.js";
import { getMetadata, postprocessThreeProperty } from "./propertyProvider.metadata.js";


import { ThreeDefaultViewMode } from "./propertyProvider.viewmode.js";
import { joinPath, splitPath } from "@needle-tools/addressing";
import { handleOf } from "../common/objectHandle.js";
import { buildAddress, encodeSegment, normalizeIncomingPath } from "../../properties/common/propertyPath.js";
import { NeedleMeta } from "../../../base/common/needle.js";
import { displayComponentName, resolveComponentName } from "./componentName.js";
import { ContextKeyExpr } from "../../contextkey/common/contextkey.js";
import { ignoreNodeMaterialProperty, isNodeLike, isNodeMaterial, isVarNode, isUniformNode, forceNodeMaterialRecompile } from "./helper.nodes.js";
import { extractTSLNodeProperties, createTSLNodeProperty } from "./propertyProvider.tsl.js";
import { nodeTitle } from "./nodegraph.tsl.js";

export interface ThreePropertyDecorations {
    object(group: IPropertyGroup, object: object, name: string): void;
    component(group: IPropertyGroup, object: Object3D, component: object, name: string, removable: boolean): void;
    material(group: IPropertyGroup, material: Material): void;
    docs(group: IPropertyGroup, url: string): void;
}

function readPropertyDescriptorValue(owner: object, key: string, desc: PropertyDescriptor): unknown {
    if ("value" in desc) {
        return desc.value;
    }
    if (desc.get) {
        return desc.get.call(owner);
    }
    return (owner as any)[key];
}

const needlePatchedBaseMaterialTextureKeys = new Set([
    "map",
    "bumpMap",
    "alphaMap",
    "normalMap",
    "displacementMap",
    "roughnessMap",
    "metalnessMap",
    "emissiveMap",
    "specularMap",
    "envMap",
    "lightMap",
    "aoMap",
    "gradientMap",
]);

function isNeedlePatchedBaseMaterialTextureSlot(material: Material, key: string): boolean {
    if (!needlePatchedBaseMaterialTextureKeys.has(key)) return false;
    if (Object.prototype.hasOwnProperty.call(material, key)) return false;

    // Needle Engine's resource tracking (enabled by default since 6.0.0-alpha)
    // replaces these slots with accessors on the BASE Material.prototype that
    // are backed by `<key>___needle` instance fields. Real slots of the concrete
    // material type are assigned in its constructor (`this.map = null`), which
    // runs the inherited setter and leaves the backing field as an own property.
    // Slots the material type never assigns (e.g. LineBasicMaterial.specularMap)
    // have no backing field — only those are engine-patch artifacts to hide.
    if (Object.prototype.hasOwnProperty.call(material, `${key}___needle`)) return false;

    let current = Object.getPrototypeOf(material);
    while (current && current !== Object.prototype) {
        if (Object.prototype.hasOwnProperty.call(current, key)) {
            return current === Material.prototype;
        }
        current = Object.getPrototypeOf(current);
    }

    return false;
}

function toUniformGroupLabel(groupName: string): string {
    switch (groupName) {
        case "aomap":
            return "AO Map";
        case "envmap":
            return "Env Map";
        case "lightmap":
            return "Light Map";
        case "normalmap":
            return "Normal Map";
        case "displacementmap":
            return "Displacement Map";
        case "emissivemap":
            return "Emissive Map";
        case "specularmap":
            return "Specular Map";
        case "metalnessmap":
            return "Metalness Map";
        case "roughnessmap":
            return "Roughness Map";
        case "gradientmap":
            return "Gradient Map";
        case "lights":
            return "Lighting";
        default:
            return groupName.charAt(0).toUpperCase() + groupName.slice(1);
    }
}

const uniformGroupNameByUniform = (() => {
    const result = new Map<string, string>();
    const groupPriority = [
        "lights",
        "fog",
        "envmap",
        "lightmap",
        "aomap",
        "bumpmap",
        "normalmap",
        "displacementmap",
        "emissivemap",
        "metalnessmap",
        "roughnessmap",
        "gradientmap",
        "specularmap",
        "points",
        "sprite",
        "common",
    ];

    for (const groupName of groupPriority) {
        const group = (UniformsLib as Record<string, Record<string, unknown> | undefined>)[groupName];
        if (!group) continue;
        for (const uniformName of Object.keys(group)) {
            if (!result.has(uniformName)) {
                result.set(uniformName, groupName);
            }
        }
    }

    return result;
})();

const MAX_GENERIC_ARRAY_ITEMS = 50;

/** First `limit` items of an iterable, without walking the rest of it. */
function takeFrom<T>(iterable: Iterable<T>, limit: number): T[] {
    const out: T[] = [];
    for (const item of iterable) {
        out.push(item);
        if (out.length >= limit) break;
    }
    return out;
}
const MAX_GENERIC_OBJECT_PROPERTIES = 60;
const MAX_NESTING_DEPTH = 10;

/**
 * Per-`createProperty()`-call cache so children of a shared object (e.g. a Texture
 * referenced by 50 materials) are only materialized once.
 *
 * - `built === false` means the entry was claimed but the recursion is still
 *   populating it (i.e. the value is somewhere on the current call stack).
 *   Consumers must treat this as a cycle and bail with empty children rather
 *   than read the partial array.
 * - `built === true` means the children array is final and can be reused.
 */
type NestedChildCacheEntry = { children: IProperty[]; built: boolean };
type NestedChildCache = Map<object, NestedChildCacheEntry>;
function isArrayIndexKey(key: string): boolean {
    return /^\d+$/.test(key);
}

function buildNestedAddress(parentAddress: string | undefined, key: string): string | undefined {
    if (!parentAddress) return undefined;
    // document grammar: an array index is a segment like any other, and a key
    // containing "/" or "~" is escaped rather than silently splitting the path
    return `${parentAddress}/${encodeSegment(key)}`;
}

function isExpandableObjectValue(value: unknown): value is Record<string, unknown> | unknown[] {
    if (!value || typeof value !== "object") return false;
    if (ArrayBuffer.isView(value)) {
        return !(value instanceof DataView);
    }
    if (value instanceof Texture) return false;
    if (value instanceof Uniform) return false;
    if (value instanceof WebGLRenderer) return false;
    if (typeof HTMLElement !== "undefined" && value instanceof HTMLElement) return false;
    if (isColorLike(value)) return false;
    if (isVectorPropertyLike({ value } as any)) return false;
    if (isMatrixLike(value)) return false;
    if (isObject3D(value) || isScene(value) || isCamera(value)) return false;
    return true;
}

function shouldExpandNestedProperty(key: string, value: unknown): boolean {
    if (!isExpandableObjectValue(value)) return false;
    if (key === "uniforms") return false;
    return true;
}

// #region EventList support

declare type EventListCallEntry = {
    enabled: boolean;
    target: unknown;
    methodName: string | null;
    arguments?: unknown[];
};

/**
 * Duck-typed check for a Needle Engine EventList instance. This file must stay
 * engine-import-free (the vanilla three.js editor shares it), so we test the runtime
 * shape: `isEventList` marker + the public `calls` entry accessor. Engines that predate
 * the `calls` accessor keep rendering as a generic object.
 */
export function isEventListValue(value: unknown): value is { calls: readonly EventListCallEntry[] } {
    const list = value as { isEventList?: boolean; calls?: unknown } | null | undefined;
    return list?.isEventList === true && Array.isArray(list.calls);
}

/**
 * Base-class members that would only be noise in an event-target method dropdown:
 * the three.js Object3D graph/transform API, the EventDispatcher API and the Needle
 * component lifecycle + query API. Heuristic allowlist-by-denylist — same spirit as
 * the engine's EventList deserializer, which accepts any method or settable property.
 */
const EVENT_METHOD_DENYLIST = new Set([
    "constructor",
    // EventDispatcher
    "addEventListener", "removeEventListener", "hasEventListener", "dispatchEvent",
    // Object3D graph & transform plumbing
    "add", "remove", "clear", "attach", "getObjectById", "getObjectByName",
    "getObjectByProperty", "getObjectsByProperty", "traverse", "traverseVisible",
    "traverseAncestors", "updateMatrix", "updateMatrixWorld", "updateWorldMatrix",
    "applyMatrix4", "applyQuaternion", "setRotationFromAxisAngle", "setRotationFromEuler",
    "setRotationFromMatrix", "setRotationFromQuaternion", "rotateOnAxis",
    "rotateOnWorldAxis", "translateOnAxis", "localToWorld", "worldToLocal",
    "getWorldPosition", "getWorldQuaternion", "getWorldScale", "getWorldDirection",
    "raycast", "clone", "copy", "toJSON", "onBeforeRender", "onAfterRender",
    "onBeforeShadow", "onAfterShadow",
    // Needle component lifecycle & query API
    "awake", "start", "earlyUpdate", "update", "lateUpdate", "onEnable", "onDisable",
    "onDestroy", "destroy", "startCoroutine", "stopCoroutine", "addComponent",
    "getComponent", "getComponents", "getComponentInChildren", "getComponentsInChildren",
    "getComponentInParent", "getComponentsInParent",
]);

/**
 * The invocable members of an event-call target: functions and property setters from
 * the prototype chain plus the target's @serializable fields (`$serializedTypes` —
 * assignable by the engine's property-style invocation). Mirrors what the engine's
 * EventList deserializer accepts at load time.
 */
function collectInvocableMemberOptions(target: unknown): PropertyMetadata["enumOptions"] {
    if (!target || typeof target !== "object") return undefined;
    const names = new Set<string>();
    const serialized = (target as { $serializedTypes?: Record<string, unknown> }).$serializedTypes;
    if (serialized) {
        for (const key of Object.keys(serialized)) names.add(key);
    }
    let proto = Object.getPrototypeOf(target);
    while (proto && proto !== Object.prototype) {
        for (const [name, desc] of Object.entries(Object.getOwnPropertyDescriptors(proto))) {
            if (name.startsWith("_") || name.startsWith("$") || name.startsWith("__")) continue;
            if (EVENT_METHOD_DENYLIST.has(name)) continue;
            if (typeof desc.value === "function" || typeof desc.set === "function") names.add(name);
        }
        proto = Object.getPrototypeOf(proto);
    }
    if (names.size === 0) return undefined;
    return [...names].sort().map(name => ({ label: name, value: name }));
}

function getNestedChildKeys(value: Record<string, unknown> | unknown[]): string[] {
    if (Array.isArray(value)) {
        const keys = new Array<string>();
        const limit = Math.min((value as unknown as { length: number }).length, MAX_GENERIC_ARRAY_ITEMS);
        for (let i = 0; i < limit; i++) {
            if (!(i in value)) continue;
            if (value[i] === undefined) continue;
            keys.push(String(i));
        }
        return keys;
    }

    if (ArrayBuffer.isView(value) && !(value instanceof DataView)) {
        const keys = new Array<string>();
        const limit = Math.min((value as unknown as { length: number }).length, MAX_GENERIC_ARRAY_ITEMS);
        for (let i = 0; i < limit; i++) {
            keys.push(String(i));
        }
        return keys;
    }

    // TSL nodes expand by OWN keys only — the prototype carries hundreds of
    // swizzle getters (.xxyx/.rrgg/…) and array-index accessors, all noise
    // (same rule as the provider's top-level TSL walk).
    if (isTSLNode(value)) {
        return Object.keys(value).slice(0, MAX_GENERIC_OBJECT_PROPERTIES);
    }

    const keys = Object.keys(PropertyUtils.getAllObjectProperties(value))
        .filter(key => key !== "__proto__")
        .slice(0, MAX_GENERIC_OBJECT_PROPERTIES);
    return keys;
}

function createCollectionEntryOwner<Source extends object, T>(source: Source, getter: (source: Source) => T, setter: (source: Source, value: T) => void): Record<string, unknown> {
    return new PropertyOwnerDefinition<Source>({
        value: { read: getter, write: (current, value) => setter(current, value as T) },
    }).bind(source);
}

function formatCollectionLabel(index: number, key?: unknown): string {
    if (key === undefined) {
        return `[${index}]`;
    }
    const keyText = typeof key === "string"
        ? key
        : typeof key === "number" || typeof key === "boolean"
            ? String(key)
            : key === null
                ? "null"
                : "object";
    return `[${index}] ${keyText}`;
}

function isReadOnlyNestedAddress(address: string | undefined): boolean {
    if (!address) return false;
    if (address === "renderer/capabilities" || address.startsWith("renderer/capabilities/")) {
        return true;
    }
    // renderer/info exposes runtime render/memory stats (triangles, calls, geometries, ...)
    // that write back nowhere. Only autoReset is a real, settable option.
    if (address.startsWith("renderer/info/")) {
        return address !== "renderer/info/autoReset";
    }
    return false;
}

function markPropertyReadOnly(property: IProperty): void {
    property.editable = false;
    property.metadata.editable = false;
    property.metadata.writeable = false;
    property.children?.forEach(markPropertyReadOnly);
}

/** Give a component group's top-level properties a stable, type-scoped
 *  `contextKey` (e.g. `component.Rigidbody.mass`) so the star feature can key off
 *  them — instance-INDEPENDENT, so starring once applies to every instance of
 *  that component type. Object/material props get their contextKey in
 *  propertyProvider.metadata.ts; component props are collected by the engine, so
 *  we stamp them here. Never overwrites an existing contextKey. */
function stampComponentContextKeys(group: PropertyGroup, componentName: string): void {
    for (const p of group.properties) {
        if (p.metadata && !p.metadata.contextKey) {
            p.metadata.contextKey = `component.${componentName}.${p.key}`;
        }
    }
}

const animationRunningRead = methodReadDefinition("isRunning");

type TextureSlot = { owner: object; key: string };

function getSlotTexture(source: TextureSlot): Record<string, unknown> | null {
    const texture = Reflect.get(source.owner, source.key);
    return texture && typeof texture === "object" ? texture : null;
}

function setSlotTextureProperty(source: TextureSlot, propertyKey: string, nextValue: unknown) {
    const texture = getSlotTexture(source);
    if (!texture) return;

    const currentValue = texture[propertyKey];
    if (
        currentValue &&
        typeof currentValue === "object" &&
        nextValue &&
        typeof nextValue === "object"
    ) {
        if ("copy" in currentValue && typeof (currentValue as any).copy === "function") {
            (currentValue as any).copy(nextValue);
            return;
        }
        if ("x" in currentValue && "y" in currentValue) {
            const currentVec = currentValue as Record<string, unknown>;
            const nextVec = nextValue as Record<string, unknown>;
            currentVec.x = nextVec.x ?? currentVec.x;
            currentVec.y = nextVec.y ?? currentVec.y;
            if ("z" in currentVec) currentVec.z = nextVec.z ?? currentVec.z;
            if ("w" in currentVec) currentVec.w = nextVec.w ?? currentVec.w;
            return;
        }
    }

    texture[propertyKey] = nextValue;
}


const texturePropertyKeys = [
    "format",
    "colorSpace",
    "channel",
    "mapping",
    "flipY",
    "repeat",
    "offset",
    "wrapS",
    "wrapT",
    "rotation",
    "center",
    "premultiplyAlpha",
    "anisotropy",
] as const;
const textureDetailsDefinition = new PropertyOwnerDefinition<TextureSlot>(Object.fromEntries(
    texturePropertyKeys.map(key => [key, {
        read: (source: TextureSlot) => getSlotTexture(source)?.[key],
        write: (source: TextureSlot, value: unknown) => setSlotTextureProperty(source, key, value),
    }]),
));
const imageDetailsDefinition = new PropertyOwnerDefinition<TextureSlot>({
    width: { read: source => (getSlotTexture(source)?.image as { width?: number } | undefined)?.width },
    height: { read: source => (getSlotTexture(source)?.image as { height?: number } | undefined)?.height },
});
const textureMoreDefinition = new PropertyOwnerDefinition<TextureSlot>({
    value: { read: getSlotTexture },
});

export class ThreePropertyReader implements IPropertyProvider {
    discovery?: PropertyDiscovery;

    withDiscovery<T>(discovery: PropertyDiscovery, run: () => T): T {
        const previous = this.discovery;
        this.discovery = discovery;
        try { return run(); } finally { this.discovery = previous; }
    }


    private static readonly hiddenMaterialKeys = new Set([
        "_context",
        "_forceRefresh",
        "_listeners",
    ]);

    constructor(
        private readonly logger: ILogService,
        private readonly propertyAccessorService: IPropertyAccessorService,
        private readonly stateProvider?: (options: GetPropertyOptions) => IPropertyStateProvider,
        readonly engine?: ThreeJs,
        readonly decorations?: ThreePropertyDecorations,
    ) {
        propertyAccessorService.registerAccessor(ThreeObjectMetadataAccessor);
        propertyAccessorService.registerAccessor(ColorPropertyAccessor);
        propertyAccessorService.registerAccessor(VectorPropertyAccessor);
        propertyAccessorService.registerAccessor(MatrixPropertyAccessor);
        propertyAccessorService.registerAccessor(ObjectReferenceAccessor);
        propertyAccessorService.registerAccessor(TexturePropertyAccessor);
        propertyAccessorService.registerAccessor(TSLInputNodeAccessor);
        propertyAccessorService.registerAccessor(AnimationTimeAccessor);
    }

    canHandle(object: object): boolean {
        // materials/textures/geometries can be inspected directly (without an
        // owning Object3D) — e.g. from the Resources panel; same for mixers and
        // raw loader results (Resources panel Animations/Models groups), and
        // TSL shader-graph nodes (revealed by clicking a node-graph card)
        return isObject3D(object) || isRenderer(object) || isMaterial(object) || isTexture(object) || isGeometry(object)
            || isAnimationMixer(object) || isAnimationClipLike(object) || isLoadedModelResult(object) || isModelProjection(object) || isTSLNode(object);
    }


    private readonly object3DIgnore = [
        "needle_isActiveInHierarchy",
        "matrixWorldNeedsUpdate",
    ]

    async getProperties(object: object, options: GetPropertyOptions): Promise<IPropertyCollection | null> {
        return this.readProperties(object as Object3D | WebGLRenderer | Material | Texture | BufferGeometry, options);
    }

    /** Synchronous so a runtime request can resolve and read without retaining a target across an await. */
    readProperties(object: Object3D | WebGLRenderer | Material | Texture | BufferGeometry, options: GetPropertyOptions): IPropertyCollection | null {

        let name = "Object";
        let type = "Object";
        if (isObject3D(object)) {
            type = object.type;
            name = object.name || object.type;
        }
        else if (isRenderer(object)) {
            type = "Renderer";
            name = "type" in object && typeof object.type === "string" ? object.type : "WebGLRenderer";
        }
        else if (isMaterial(object)) {
            type = object.type || "Material";
            name = object.name || type;
        }
        else if (isTexture(object)) {
            // Texture.type is a numeric data-type constant, not a string
            type = object.constructor?.name || "Texture";
            name = object.name || type;
        }
        else if (isGeometry(object)) {
            type = (object as BufferGeometry).type || "BufferGeometry";
            name = (object as BufferGeometry).name || type;
        }
        else if (isAnimationMixer(object)) {
            type = "AnimationMixer";
            name = "AnimationMixer";
            try {
                // the root's name alone — the group header right below already
                // says "AnimationMixer", so a "Mixer (…)" prefix repeats it (same
                // reason the Resources row dropped it)
                const root = object.getRoot?.();
                if (root?.name) name = root.name;
            } catch { /* host object — stay defensive */ }
        }
        else if (isAnimationClipLike(object)) {
            // AnimationClip carries no `type` field; its name is the clip name ("Idle")
            type = "AnimationClip";
            name = object.name || type;
        }
        else if (isLoadedModelResult(object)) {
            const isGltf = !!(object as { parser?: unknown; asset?: unknown }).parser
                && !!(object as { asset?: unknown }).asset;
            type = isGltf ? "GLTF" : "File";
            name = type;
        }
        else if (isModelProjection(object)) {
            // the inert model projection registered as the "model" resource
            type = object.json ? "GLTF" : "Model";
            name = object.name || type;
        }
        else if (isTSLNode(object)) {
            // Prefer the constructor-name `type` (e.g. "ContextNode"); some nodes
            // (PassNode & co.) lack it (absent/minified), so fall back to the
            // shared node title used by the graph cards ("Pass", "Operator …").
            const nodeType = (object as { type?: string }).type;
            type = (typeof nodeType === "string" && nodeType) ? nodeType : nodeTitle(object as unknown as Parameters<typeof nodeTitle>[0]);
            name = (object as { name?: string }).name || type;
        }

        const collection = new PropertyCollection(name, []);

        const state = this.stateProvider?.(options) ?? (options.viewMode === PropertyViewMode.DEFAULT
            ? new ThreeDefaultViewMode() : { isHidden: () => false });

        // Shared cache across all top-level properties of this inspection — lets
        // siblings reuse children for the same JS object (e.g. a Texture referenced
        // by both `material.map` and `material.normalMap` on the same mesh).
        const nestedCache: NestedChildCache = new Map();

        const group = new PropertyGroup(type, "Object");
        this.decorations?.object(group, object, name);
        group.addCategory("three")
        // a directly-inspected material builds its own group (with material actions)
        if (!isMaterial(object)) {
            collection.groups.push(group);
        }


        // #region Renderer Properties
        if (isRenderer(object)) {
            group.name = "Renderer";

            let docsUrl: string | undefined = undefined;
            if (object.isWebGLRenderer) {
                docsUrl = `https://threejs.org/docs/#WebGLRenderer`;
            }
            else {
                docsUrl = `https://threejs.org/docs/#WebGPURenderer`;
            }
            if (docsUrl) {
                this.decorations?.docs(group, docsUrl);
            }

            const props = PropertyUtils.getAllObjectProperties(object);
            for (const key in props) {
                if (state.isHidden(object, key)) {
                    continue;
                }
                const property = this.createPropertyWithState(object, key, state, nestedCache);
                if (property) {
                    group.properties.push(property);
                }
            }
            // const prop = new class extends Property {
            //     constructor() {
            //         super(object, "renderTarget", "texture", false, true, false, {
            //             description: "The current render target. If null, the default framebuffer is used.",
            //         },);
            //         this.label = "renderTarget";
            //     }
            //     get value() {
            //         return object.getRenderTarget();
            //     }
            // }();
            // group.properties.push(prop);
        }
        // #region TSL node (shader-graph node objects revealed from the node
        // graph): a generic full walk — child nodes render as nested objects
        // the user can expand/log, which beats any value string on the card.
        else if (isTSLNode(object)) {
            // the shared group is created with the literal name "Object" — use
            // the node's constructor-name type ("RenderOutputNode") instead
            group.name = type;
            // docs SEARCH for the tsl name derived from the type ("ContextNode"
            // → ?q=context) — same fallback pattern as the graph cards
            const base = type.replace(/Node$/, "");
            const docsUrl = `https://threejs.org/docs/?q=${encodeURIComponent(base.charAt(0).toLowerCase() + base.slice(1))}`;
            this.decorations?.docs(group, docsUrl);

            // OWN enumerable keys only — NOT the prototype chain: TSL nodes
            // define getters for every swizzle permutation (xxyx, rrgg, sstp,
            // … hundreds) plus chainable method-likes, all noise. The node's
            // real state lives in its own fields (what a console dump shows) —
            // INCLUDING underscore internals like _toneMapping, so the default
            // hidden-key rules are deliberately NOT applied here.
            for (const key of Object.keys(object)) {
                const property = this.createPropertyWithState(object, key, state, nestedCache);
                if (property) {
                    const handle = handleOf(object as { id?: unknown; uuid?: unknown });
                    if (handle) {
                        property.metadata.address = buildAddress(handle, [key]);
                        property.metadata.addressOwner = object;
                    }
                    group.properties.push(property);
                }
            }
        }
        // #region Object3D
        else if (isObject3D(object)) {
            if (object.type && typeof object.type === "string" && object.type.length > 0) {
                group.name = object.type;
            }

            const docsUrl = `https://threejs.org/docs/#${group.name}`;
            this.decorations?.docs(group, docsUrl);

            const props = PropertyUtils.getAllObjectProperties(object);


            for (const _key in props) {

                if (this.object3DIgnore.includes(_key)) continue;

                if (isScene(object) && _key === "autoUpdate") {
                    // autoUpdate was renamed to matrixWorldAutoUpdate in r144. Skip to avoid three warnings flood e.g. in https://next.junni.co.jp/
                    continue;
                }

                switch (_key) {
                    case "geometry___needle":
                    case "material___needle":
                        continue;
                }

                let propertyOwner = object as object;
                let propertyKey = _key;
                if (_key === "layers") {
                    if (isObject3D(object)) {
                        this.discovery?.path(object, ["layers"]);
                        propertyOwner = object.layers;
                        propertyKey = "mask";
                    }
                }

                if (state.isHidden(object, propertyKey)) {
                    continue;
                }

                const property = this.createPropertyWithState(propertyOwner, propertyKey, state, nestedCache);
                if (property) {
                    group.properties.push(property);

                    if (isPerspectiveCamera(propertyOwner)) {
                        switch (propertyKey) {
                            case "fov":
                            case "near":
                            case "far":
                            case "aspect":
                            case "zoom":
                            case "focus":
                            case "filmGauge":
                            case "filmOffset":
                                property.onDidChange(() => {
                                    propertyOwner.updateProjectionMatrix();
                                });
                                break;
                        }
                    }
                    // TODO: show background and environment texture properties!
                    else if (isScene(propertyOwner)) {
                        switch (propertyKey) {
                            case "background":
                            case "environment":
                                Internal.addTextureProperties(object, object, propertyKey, property, propertyKey, this, state);
                                break;
                        }
                    }
                }
            }

            // #region Material
            const materials = Internal.collectMaterialProperties(object, this, this.propertyAccessorService, state, this.logger);
            if (materials) {
                for (const matprops of materials) {
                    if (matprops) collection.groups.push(matprops);
                }
            }

            this.discovery?.path(object, ["userData", "components"]);
            this.discovery?.path(object, ["userData", "gltfExtensions", "NEEDLE_components", "builtin_components"]);
            // #region Needle Components
            if (object.userData?.components) {
                const components = object.userData.components as object[];
                if (Array.isArray(components)) {
                    for (const [componentIndex, comp] of components.entries()) {
                        this.discovery?.member(components, String(componentIndex), comp);
                        this.discovery?.watch(comp);
                        const needleComponent = comp as Needle.MaybeNeedleRuntimeComponent;
                        const componentName = displayComponentName(comp);
                        // id must be UNIQUE PER INSTANCE — two components of the same
                        // type on one object are legal (two colliders), and same-name
                        // ids crash keyed UI lists (Svelte each_key_duplicate)
                        const componentGuid = (needleComponent as { guid?: string }).guid;
                        const componentsGroup = new PropertyGroup(`${componentName}-${componentGuid ?? componentIndex}`, componentName);
                        this.decorations?.component(componentsGroup, object, comp, name, true);
                        componentsGroup.addCategory("components");
                        componentsGroup.icon = "component";
                        collection.groups.push(componentsGroup);

                        const enabledProperty = new Property(comp, "enabled", "boolean", {});
                        componentsGroup.properties.push(enabledProperty);

                        Needle.collectComponentProperties("runtime", needleComponent, this, componentsGroup, state, this.logger);
                        stampComponentContextKeys(componentsGroup, componentName);
                    }
                }
            }
            // glTF built-in components
            // `userData?.` for the same reason as the `components` branch above:
            // reaching here only means that branch was falsy, which includes the
            // case where there is no userData at all.
            else if (object.userData?.gltfExtensions?.NEEDLE_components) {
                const components = object.userData.gltfExtensions.NEEDLE_components.builtin_components;
                this.logger.debug("Found builtin_components in userData:", components);
                for (const [componentIndex, comp] of (components as object[]).entries()) {
                    this.discovery?.member(components, String(componentIndex), comp);
                    this.discovery?.watch(comp);
                    const needleComponent = comp as Needle.NeedleGltfComponent;
                    const componentName = needleComponent.name;
                    // per-instance id — see the runtime-components loop above
                    const componentGuid = (needleComponent as { guid?: string }).guid;
                    const componentsGroup = new PropertyGroup(`${componentName}-gltf-${componentGuid ?? componentIndex}`, `${componentName} (glTF)`);
                    this.decorations?.component(componentsGroup, object, comp, name, false);
                    componentsGroup.addCategory("components");
                    componentsGroup.icon = "component";
                    collection.groups.push(componentsGroup);

                    const enabledProperty = new Property(comp, "enabled", "boolean", {});
                    componentsGroup.properties.push(enabledProperty);

                    Needle.collectComponentProperties("gltf", needleComponent, this, componentsGroup, state, this.logger);
                    stampComponentContextKeys(componentsGroup, componentName);
                }
            }

        }
        // #endregion Object3D

        // #region Direct resource inspection (no owning Object3D — e.g. Resources panel)
        else if (isMaterial(object)) {
            const matGroup = Internal.getMaterialProperties(null, object, this, this.propertyAccessorService, state, this.logger);
            if (matGroup) {
                collection.groups.push(matGroup);
            }
        }
        else if (isTexture(object) || isGeometry(object)) {
            group.name = type;
            if (isTexture(object)) group.icon = "texture";

            const docsUrl = `https://threejs.org/docs/#api/en/${isTexture(object) ? "textures" : "core"}/${type}`;
            this.decorations?.docs(group, docsUrl);

            // A resource inspected on its own IS the address root, exactly like a
            // material picked from Resources. Without this its rows carried no
            // address at all: readable, never editable — a session clicked a texture,
            // found nothing it could act on, and went guessing which material used it.
            const rootAddress = this.addressRootFor(object);

            const props = PropertyUtils.getAllObjectProperties(object);
            for (const key in props) {
                if (state.isHidden(object, key)) {
                    continue;
                }
                const property = this.createPropertyWithState(object as unknown as Record<string, unknown>, key, state, nestedCache);
                if (property) {
                    if (rootAddress) property.metadata.address = buildAddress(rootAddress, [key]);
                    group.properties.push(property);
                }
            }
        }
        else if (isAnimationMixer(object)) {
            group.name = "AnimationMixer";

            this.discovery?.path(object, ["_root"]);
            this.discovery?.path(object, ["_actions"]);
            const mixer = object as unknown as Record<string, unknown>;
            // time scrubs through AnimationTimeAccessor (setTime — raw writes
            // don't retime actions); timeScale writes directly
            for (const key of ["time", "timeScale"]) {
                const property = this.createPropertyWithState(mixer, key, state, nestedCache);
                if (property) group.properties.push(property);
            }

            // WHAT it animates. A mixer is meaningless without its root, and the
            // root is the only way back to the object from here — the clips it
            // plays may well come from a different file than the rig. Stored as
            // `_root` (getRoot() is the public accessor), so it needs un-hiding
            // from the underscore-internals rule.
            const rootProperty = this.createPropertyWithState(mixer, "_root", state, nestedCache);
            if (rootProperty) {
                rootProperty.label = "root";
                rootProperty.hidden = false;
                rootProperty.editable = false; // reseating a live mixer is not an edit we support
                group.properties.push(rootProperty);
            }

            // ALL clips on the mixer's root (root.animations) — apps often
            // create actions lazily per clip, so the action groups below can
            // be a subset of what exists; this list shows every clip either way
            try {
                const root = (object as { getRoot?(): Record<string, unknown> | undefined }).getRoot?.();
                const rootAnimations = root?.animations;
                if (root && Array.isArray(rootAnimations) && rootAnimations.length > 0) {
                    const clipsProperty = this.createPropertyWithState(root, "animations", state, nestedCache);
                    if (clipsProperty) {
                        (clipsProperty as { label?: string }).label = "clips";
                        group.properties.push(clipsProperty);
                    }
                }
            } catch { /* host object — stay defensive */ }

            // one group per action: the clip + its play state IS the useful
            // surface of a mixer (all keys below are public AnimationAction
            // API). paused/enabled/weight/timeScale/loop write cleanly (the
            // mixer respects them continuously; loop is an enum dropdown via
            // metadata). time + repetitions write through
            // AnimationTimeAccessor (update(0) pose refresh / setLoop);
            // repetitions is Infinity while looping without a count — the
            // number renderer shows non-finite values as text.
            const actions = (object as { _actions?: unknown[] })._actions;
            if (Array.isArray(actions)) {
                let index = 0;
                for (const action of actions) {
                    if (!action || typeof action !== "object") continue;
                    this.discovery?.member(actions, String(index), action);
                    this.discovery?.path(action, ["_clip"]);
                    const clip = (action as { _clip?: { name?: string; duration?: number } })._clip;
                    if (clip) this.discovery?.depend(clip, "duration", clip.duration);
                    const clipName = (typeof clip?.name === "string" && clip.name) || `Action ${index}`;
                    const duration = typeof clip?.duration === "number" ? ` — ${clip.duration.toFixed(2)}s` : "";
                    // id FIRST, display name second (PropertyGroup(id, name, …)):
                    // groups need distinct display names or the inspector shows
                    // one merged "AnimationAction" group (last action wins)
                    const actionGroup = new PropertyGroup(`animation-action-${index}`, `${clipName}${duration}`, [], []);
                    actionGroup.addCategory("three");
                    // live "is it playing" — a METHOD on the action, surfaced as
                    // a boolean (value re-evaluates on every read). EDITABLE:
                    // writes route through AnimationTimeAccessor — on = play()
                    // (activates an action `paused` can't start), off = stop()
                    // (deactivate + reset).
                    const runningProperty = new Property(action, "isRunning", "boolean", {
                        editable: true, readable: true, writeable: true,
                        description: "isRunning(): whether the mixer is currently advancing this action. Toggle ON to play() the action (enables + unpauses), OFF to stop() it (deactivates and resets).",
                    }, this.propertyAccessorService, null, null, animationRunningRead);
                    actionGroup.properties.push(runningProperty);
                    for (const key of ["paused", "enabled", "weight", "time", "timeScale", "loop", "repetitions"]) {
                        const property = this.createPropertyWithState(action as Record<string, unknown>, key, state, nestedCache);
                        if (property) {
                            actionGroup.properties.push(property);
                        }
                    }
                    // the clip itself (name/duration/tracks) — explicitly surfaced
                    // despite its underscore key
                    const clipProperty = this.createPropertyWithState(action as Record<string, unknown>, "_clip", state, nestedCache);
                    if (clipProperty) {
                        (clipProperty as { label?: string }).label = "clip";
                        (clipProperty as { hidden?: boolean }).hidden = false;
                        actionGroup.properties.push(clipProperty);
                    }
                    collection.groups.push(actionGroup);
                    index++;
                }
            }
        }
        else if (isAnimationClipLike(object)) {
            // The clip ASSET (selected in the Resources panel). A clip has no
            // is* flag and no `type`, so without this branch it produced an empty
            // inspector. Generic walk over its own keys (name/duration/blendMode/
            // uuid/tracks); `tracks` expands lazily like any nested array, which
            // is where the per-channel detail lives (each KeyframeTrack's name is
            // the node + property it drives, e.g. "mixamorigHips.quaternion").
            group.name = "AnimationClip";
            const clip = object as unknown as Record<string, unknown>;
            const props = PropertyUtils.getAllObjectProperties(clip);
            for (const key in props) {
                if (state.isHidden(clip, key)) {
                    continue;
                }
                const property = this.createPropertyWithState(clip, key, state, nestedCache);
                if (property) {
                    group.properties.push(property);
                }
            }
        }
        else if (isLoadedModelResult(object) || isModelProjection(object)) {
            // the RAW loader result (parsed gltf: scene/animations/asset/parser…)
            // OR the inert model projection that replaced it as the "model"
            // resource (name/animationNames/counts/json) — same generic walk;
            // nested children are lazy so even the json structure is safe to expose.
            group.name = name;
            const props = PropertyUtils.getAllObjectProperties(object);
            for (const key in props) {
                if (state.isHidden(object, key)) {
                    continue;
                }
                const property = this.createPropertyWithState(object as unknown as Record<string, unknown>, key, state, nestedCache);
                if (property) {
                    // a loaded file / its projection is an inert snapshot — nothing
                    // here is a live editable value (recurses to children).
                    markPropertyReadOnly(property);
                    group.properties.push(property);
                }
            }
        }
        // #endregion Direct resource inspection

        return collection;
    }



    createProperty(object: object, key: string): IProperty | null {
        return this.createPropertyInternal(object as Record<string, unknown>, key, 0, [], undefined, new Map());
    }

    createPropertyWithState(
        object: object,
        key: string,
        state: IPropertyStateProvider,
        cache: NestedChildCache = new Map(),
    ): IProperty | null {
        return this.createPropertyInternal(object as Record<string, unknown>, key, 0, [], state, cache);
    }

    private createPropertyInternal(
        object: Record<string, unknown>,
        key: string,
        depth: number,
        ancestors: object[],
        state?: IPropertyStateProvider,
        cache?: NestedChildCache,
    ): IProperty | null {
        if (state?.isHidden(object, key)) {
            return null;
        }

        this.discovery?.watch(object);
        let label: string | undefined = undefined;

        const desc = PropertyUtils.findPropertyDescriptor(object, key);
        if (!desc) {
            this.logger.warn(`ThreePropertyReader: No property descriptor found for key ${key} on object`);
            return null;
        }

        if (isMaterial(object) && key.endsWith("___needle")) {
            // the ___needle properties are internal metadata, skip them
            return null;
        }

        // Try to read the value, but catch Proxy invariant violations
        // This can happen when the object is wrapped in a proxy (e.g., Threlte/Svelte reactivity)
        // and the property is non-configurable with an object value.
        // Also catches getter errors (e.g., third-party toJSON / "Method not implemented.")
        let value: unknown;
        try {
            value = readPropertyDescriptorValue(object, key, desc);
        }
        catch (err) {
            if (isProxyInvariantError(err)) {
                this.logger.debug(`ThreePropertyReader: Skipping property '${key}' due to proxy invariant violation`);
            } else {
                this.logger.warn(`ThreePropertyReader: Skipping property '${key}' due to error:`, err);
            }
            return null;
        }
        this.discovery?.member(object, key, value);
        // `typeof null` is "object", so inferring the type from the VALUE declared
        // every null field as an object property — and the Inspector renders an
        // object type as an assignable reference slot. That offered a hierarchy
        // picker on `name`, `nodeType`, `cacheKey` … inviting you to assign a Mesh
        // into a field that cannot hold one. A null carries no type information;
        // say that instead of claiming "object". Slots that genuinely accept a
        // reference while unset (a texture `map`, typed object properties) declare
        // it in metadata (`propertyAcceptsImage` and friends), so they are
        // unaffected — they never depended on this inference.
        let type = value === null || value === undefined ? "null" : typeof value;
        if (isMatrixLike(value)) {
            type = "matrix";
        }

        // Functions are skipped: every three object carries dozens of METHODS and
        // listing them would drown the inspector. An ARRAY ENTRY is different —
        // there it is DATA. A TSL Loop keeps its body as params[1] (a callable
        // proxy), and dropping it made a "[ 2 ]" array unfold into a single row,
        // hiding the one thing worth opening the array for.
        let functionEntry = false;
        if (type === "function") {
            if (!isArrayIndexKey(key)) return null;
            functionEntry = true;
            // rendered as a reference (name badge), never stringified into the
            // text input — its source could be the whole shader body
            type = "object";
        }


        let volumeParameterAnchor: { guid: string; key: string } | null = null;
        if (Needle.isNeedleVolumeParameter(value)) {
            // deep-address the OWNING component before retargeting: the parameter
            // object this row will own is unaddressable, so without the anchor the
            // edit was never captured as an operation (#97, bloom threshold).
            // Identity precedence must match componentIdentityGuid (documentTarget):
            // the file-baked editor guid is the shared identity, the runtime guid
            // re-mints per load.
            const host = object as { guid?: unknown; needle_editor_guid?: unknown };
            const guid = typeof host.needle_editor_guid === "string" && host.needle_editor_guid.length > 0
                ? host.needle_editor_guid
                : typeof host.guid === "string" && host.guid.length > 0 && host.guid !== "invalid" ? host.guid : null;
            if (guid) volumeParameterAnchor = { guid, key };
            object = object[key] as any;
            label = key;
            key = "value";
            value = (object as any)[key];
            type = typeof value;
        }
        // For @serializable properties undefined types are primitives
        else if (Needle.isNeedleRuntimeComponent(object)) {
            if (typeof type === "undefined" || type === null) {
                const valueType = typeof value;
                if (valueType === "string" || valueType === "number" || valueType === "boolean") {
                    type = valueType;
                }
            }
            if (!type) {
                return null; // cannot determine type
            }
        }

        let editable = true;
        if (desc.get && desc.set === undefined) {
            editable = false; // Read-only if no setter
        }
        if (functionEntry) {
            editable = false; // there is no sane way to assign a function from the UI
        }
        let hidden = false;
        // TSL nodes keep their underscore internals visible (_toneMapping,
        // _cacheKey, …) — console-dump fidelity is the point of revealing them
        if (key.startsWith("_") && !isMaterial(object) && !isTSLNode(object)) {
            hidden = true;
        }
        if (isMaterial(object) && ThreePropertyReader.hiddenMaterialKeys.has(key)) {
            hidden = true;
        }

        // For materials
        if (key === "needsUpdate" && !desc.get) {
            type = "boolean";
        }
        if (isNodeLike(value)) {
            if (value.nodeType)
                type = value.nodeType as any;
            if (isVarNode(value)) {
                type = value.node.nodeType as any;
            }
        }
        if (isEventListValue(value)) {
            type = "eventlist" as any;
        }

        const readable = desc.get !== undefined || desc.value !== undefined;
        const writeable = desc.set !== undefined || desc.writable || false;
        const meta = getMetadata(object, key, value);
        meta.editable = editable;
        meta.writeable = writeable;
        meta.readable = readable;
        const property = new Property(object, key, type, meta, this.propertyAccessorService);
        this.discovery?.metadata(property, getMetadata, meta);
        property.hidden = hidden;
        if (label !== undefined) property.label = label;
        if (volumeParameterAnchor) {
            property.metadata.address = buildAddress(`component:${volumeParameterAnchor.guid}`, [volumeParameterAnchor.key, "value"]);
        }

        if (type === "matrix") {
            property.metadata.childrenExpanded = false;
            property.metadata.childrenLabel = `${property.label} matrix`;
        }

        if (isMaterial(object)) {
            // When a material property changes, we need to set needsUpdate to true
            property.onDidChange((e) => {
                // When the version changes we don't need to set needsUpdate, as it is done automatically
                if (key === "version" || key === "needsUpdate" || key === "uniformsNeedUpdate") {
                    return;
                }
                // Only trigger needsUpdate when the actual value changed, not for metadata changes (e.g., hidden)
                if (e.value !== true) {
                    return;
                }
                const mat = object as Material;
                mat.needsUpdate = true;
            });
        }

        if (type === "eventlist") {
            // purpose-built children (per-call enabled/target/method/argument) — the
            // generic object walk would surface the EventList internals instead
            this.attachEventListCallProperties(property);
            property.onDidChange((e) => {
                if (e.value === undefined) return;
                this.attachEventListCallProperties(property);
            });
        }
        else if (type !== "matrix") {
            // Initial build during this createProperty() call uses the shared cache.
            // Late refreshes (fired by onDidChange after the call returned) intentionally
            // run cache-less so we don't keep a strong reference to every object the
            // initial walk visited.
            this.attachNestedChildProperties(property, property.value, depth, ancestors, state, undefined, cache);
            const refreshNestedChildProperties = () => {
                this.attachNestedChildProperties(property, property.value, depth, ancestors, state);
            };
            property.onDidChange((e) => {
                if (e.value === undefined) return;
                refreshNestedChildProperties();
            });
        }
        postprocessThreeProperty(property);
        return property;
    }

    attachNestedChildProperties(
        property: IProperty,
        value: unknown,
        depth: number,
        ancestors: object[],
        state?: IPropertyStateProvider,
        options?: {
            filterChild?: (child: IProperty) => boolean;
        },
        _cache?: NestedChildCache,
    ): void {
        // a VALUE refresh on a live property must not reset the user's expansion:
        // this reruns on every onDidChange (e.g. adding an array entry), and
        // resetting to the collapsed default folded the row the user was editing
        // in (Marcel 2026-07-31: "when i add an item the property does FOLD").
        // Only the initial build (still undefined) gets the collapsed default.
        const wasExpanded = property.metadata.childrenExpanded;
        property.children = undefined;
        property.metadata.childrenExpanded = undefined;
        property.metadata.childrenLabel = undefined;

        if (depth >= MAX_NESTING_DEPTH) return;
        if (!shouldExpandNestedProperty(property.key, value)) return;
        if (!value || typeof value !== "object") return;
        this.discovery?.member(property.owner, property.key, value);
        property.metadata.childrenExpanded = wasExpanded ?? false;
        property.metadata.childrenLabel = Array.isArray(value)
            ? `${property.label} items`
            : `${property.label} details`;

        // Lazy children: building the entire nested subtree eagerly (up to
        // MAX_NESTING_DEPTH levels of getters, metadata and Property instances)
        // froze the UI for hundreds of milliseconds when selecting objects with
        // large nested structures — even though nested groups start collapsed
        // and are mostly never opened. Materialize on first access instead
        // (typically when the inspector renders the row that shows them).
        //
        // Note: materialization runs cache-less (like the late refreshes above)
        // so we don't keep strong references to every object of the initial
        // walk. Cycle protection is handled by the `ancestors` chain.
        let materialized = false;
        let built: IProperty[] | undefined = undefined;
        const provider = this;
        const materialize = (): IProperty[] | undefined => {
            if (materialized) return built;
            materialized = true;
            const children = provider.createChildPropertiesForObject(
                value as Record<string, unknown> | unknown[],
                property.metadata.address,
                depth,
                ancestors,
                state,
                options,
            );
            built = children.length > 0 ? children : undefined;
            if (built && property.metadata.addressOwner) {
                for (const child of built) child.metadata.addressOwner ??= property.metadata.addressOwner;
            }
            if (built) notifyPropertyChildrenMaterialized(built);
            return built;
        };
        Object.defineProperty(property, "children", {
            configurable: true,
            enumerable: true,
            get: materialize,
            set: (v: IProperty[] | undefined) => {
                materialized = true;
                built = v;
            },
        });
        (property as { peekChildren?: () => IProperty[] | undefined }).peekChildren =
            () => (materialized ? built : undefined);
    }

    /**
     * Children for an "eventlist" property: one enabled/target/method/argument
     * property set per callback entry, marked with `metadata.eventCall` so the
     * inspector widget can lay them out as Unity-style rows. All four route edits
     * through the normal accessor/undo pipeline. Rebuilds itself when a target
     * changes (the method options depend on the CURRENT target).
     */
    attachEventListCallProperties(property: IProperty): void {
        const value = property.value;
        // same expansion-preserving rule as attachNestedChildProperties: this
        // rebuild runs on every change (add call, retarget) and must not fold
        // the row the user is editing in
        const wasExpanded = property.metadata.childrenExpanded;
        property.children = undefined;
        property.metadata.childrenExpanded = undefined;
        property.metadata.childrenLabel = undefined;
        if (!isEventListValue(value)) return;
        property.metadata.childrenExpanded = wasExpanded;

        this.discovery?.member(property.owner, property.key, value);
        this.discovery?.path(value, ["calls"]);
        const calls = value.calls;
        property.metadata.objectValuePlaceholderString = calls.length === 1 ? "1 call" : `${calls.length} calls`;
        const rebuild = () => {
            this.attachEventListCallProperties(property);
            // structural notification (no `value` flag): updates the widget without
            // re-triggering this rebuild via the parent's own value-change hook
            property.notifyChanged();
        };

        const children = new Array<IProperty>();
        for (let index = 0; index < calls.length; index++) {
            const call = calls[index] as unknown as Record<string, unknown>;
            this.discovery?.member(calls, String(index), call);
            this.discovery?.watch(call);
            this.discovery?.path(call, ["target"]);
            this.discovery?.path(call, ["arguments"]);
            const mark = (child: Property, role: "enabled" | "target" | "method" | "argument") => {
                child.metadata.eventCall = { index, role };
                child.metadata.inset = true;
                children.push(child);
                return child;
            };

            const enabled = new Property(call, "enabled", "boolean", { editable: true }, this.propertyAccessorService);
            enabled.label = "enabled";
            mark(enabled, "enabled");

            const target = new Property(call, "target", "object", { editable: true }, this.propertyAccessorService);
            target.label = "target";
            mark(target, "target");
            target.onDidChange((e) => {
                if (e.value !== true) return;
                rebuild();
            });

            const method = new Property(call, "methodName", "string", {
                editable: true,
                enumOptions: collectInvocableMemberOptions(call.target),
            }, this.propertyAccessorService);
            method.label = "method";
            mark(method, "method");

            // single custom argument (arguments[0]) — the common case; multi-argument
            // entries keep their extra values, we just don't edit them yet
            const argOwner = createCollectionEntryOwner(
                call,
                current => (current.arguments as unknown[] | undefined)?.[0],
                (current, newValue) => {
                    const args = current.arguments as unknown[] | undefined;
                    if (args) args[0] = newValue;
                    else current.arguments = [newValue];
                });
            const argValue = (call.arguments as unknown[] | undefined)?.[0];
            const argType = argValue === null || argValue === undefined ? "string" : typeof argValue;
            const argument = new Property(argOwner, "value", argType, { editable: true }, this.propertyAccessorService);
            argument.label = "argument";
            mark(argument, "argument");
        }

        property.children = children;
        if (children.length > 0) notifyPropertyChildrenMaterialized(children);
    }

    createChildPropertiesForObject(
        value: Record<string, unknown> | unknown[],
        parentAddress: string | undefined,
        depth: number,
        ancestors: object[],
        state?: IPropertyStateProvider,
        options?: {
            filterChild?: (child: IProperty) => boolean;
        },
        cache?: NestedChildCache,
    ): IProperty[] {
        if (ancestors.includes(value)) return [];
        this.discovery?.watch(value);

        if (value instanceof Map) {
            return this.createChildPropertiesForMap(value, parentAddress, depth, ancestors, state, options, cache);
        }

        if (value instanceof Set) {
            return this.createChildPropertiesForSet(value, parentAddress, depth, ancestors, state, options, cache);
        }

        const keys = getNestedChildKeys(value);
        if (keys.length <= 0) return [];

        const nextAncestors = [...ancestors, value];
        const children = new Array<IProperty>();

        for (const childKey of keys) {
            if (isMaterial(value) && isNeedlePatchedBaseMaterialTextureSlot(value, childKey)) {
                continue;
            }

            const childProperty = this.createPropertyInternal(
                value as Record<string, unknown>,
                childKey,
                depth + 1,
                nextAncestors,
                state,
                cache,
            );
            if (!childProperty) continue;

            childProperty.metadata.inset = true;
            childProperty.metadata.address = buildNestedAddress(parentAddress, childKey) || childProperty.metadata.address;
            if (isReadOnlyNestedAddress(childProperty.metadata.address)) {
                markPropertyReadOnly(childProperty);
            }
            if (isArrayIndexKey(childKey)) {
                childProperty.label = `[${childKey}]`;
            }
            if (options?.filterChild && !options.filterChild(childProperty)) {
                continue;
            }
            children.push(childProperty);
        }

        return children;
    }

    private createChildPropertiesForMap(
        value: Map<unknown, unknown>,
        parentAddress: string | undefined,
        depth: number,
        ancestors: object[],
        state?: IPropertyStateProvider,
        options?: {
            filterChild?: (child: IProperty) => boolean;
        },
        cache?: NestedChildCache,
    ): IProperty[] {
        // Pull only what we show. `Array.from(map.entries())` copied the WHOLE map
        // before slicing — on three's WebGPU timestamp pool (93,000 entries and
        // growing) that froze the page just for opening the object.
        const entries = takeFrom(value.entries(), MAX_GENERIC_ARRAY_ITEMS);
        const nextAncestors = [...ancestors, value];
        const children = new Array<IProperty>();

        for (let index = 0; index < entries.length; index++) {
            const [entryKey, entryValue] = entries[index];
            this.discovery?.entry(value, index, entryKey, entryValue);
            const source = { map: value, key: entryKey };
            this.discovery?.record(source, source, { key: { kind: "entry", owner: this.discovery.source(value), index, part: "key" } });
            const entryOwner = createCollectionEntryOwner(
                source,
                current => current.map.get(current.key),
                (current, newValue) => {
                    current.map.set(current.key, newValue);
                },
            );
            const childProperty = this.createPropertyInternal(
                entryOwner,
                "value",
                depth + 1,
                nextAncestors,
                state,
                cache,
            );
            if (!childProperty) continue;

            childProperty.metadata.inset = true;
            childProperty.metadata.address = buildNestedAddress(parentAddress, String(index)) || childProperty.metadata.address;
            childProperty.label = formatCollectionLabel(index, entryKey);
            if (options?.filterChild && !options.filterChild(childProperty)) {
                continue;
            }
            children.push(childProperty);
        }

        return children;
    }

    private createChildPropertiesForSet(
        value: Set<unknown>,
        parentAddress: string | undefined,
        depth: number,
        ancestors: object[],
        state?: IPropertyStateProvider,
        options?: {
            filterChild?: (child: IProperty) => boolean;
        },
        cache?: NestedChildCache,
    ): IProperty[] {
        const entries = takeFrom(value.values(), MAX_GENERIC_ARRAY_ITEMS);
        const nextAncestors = [...ancestors, value];
        const children = new Array<IProperty>();

        for (let index = 0; index < entries.length; index++) {
            this.discovery?.entry(value, index, entries[index], entries[index]);
            const source = { set: value, index };
            this.discovery?.record(source, source);
            const entryOwner = createCollectionEntryOwner(
                source,
                current => takeFrom(current.set.values(), current.index + 1)[current.index],
                (current, newValue) => {
                    const currentEntries = takeFrom(current.set.values(), current.index + 1);
                    const previousValue = currentEntries[current.index];
                    if (previousValue !== undefined) {
                        current.set.delete(previousValue);
                    }
                    current.set.add(newValue);
                },
            );
            const childProperty = this.createPropertyInternal(
                entryOwner,
                "value",
                depth + 1,
                nextAncestors,
                state,
                cache,
            );
            if (!childProperty) continue;

            childProperty.metadata.inset = true;
            childProperty.metadata.address = buildNestedAddress(parentAddress, String(index)) || childProperty.metadata.address;
            childProperty.label = formatCollectionLabel(index);
            if (options?.filterChild && !options.filterChild(childProperty)) {
                continue;
            }
            children.push(childProperty);
        }

        return children;
    }

    /**
     * The handle to root an inspected object's addresses at, or null when nothing
     * can resolve it.
     *
     * Verified through the ENGINE's own lookup — the one the write path uses — so
     * an address is minted only when passing it back reaches this same object. A
     * resource that is in no scene and no registry stays addressless instead of
     * being handed a token that fails on use, and starts working by itself once it
     * is registered.
     */
    private addressRootFor(object: object): string | null {
        const handle = handleOf(object as { id?: unknown; uuid?: unknown });
        if (!handle) return null;
        try {
            return this.engine?.findObjectById?.(handle) === object ? handle : null;
        } catch {
            return null;
        }
    }

    async findProperty(address: PropertyAddress): Promise<IProperty | null> {
        const obj = address.objectId;

        const object = address.object ?? (this.engine?.findObjectById ? this.engine.findObjectById(obj) : null);
        if (!object) {
            this.logger.warn(`ThreePropertyReader: No object found with ID ${obj}`);
            return null;
        }

        this.logger.debug(`ThreePropertyReader: Finding property ${address.propertyPath} on object with ID ${obj}`);

        let currentObject: object = object as object;
        let material = isNodeMaterial(object) ? object : undefined;
        // rows mint document paths; a model still types dots — read both
        const segments = splitPath(normalizeIncomingPath(address.propertyPath) ?? address.propertyPath);
        for (let i = 0; i < segments.length; i++) {
            const segment = segments[i];
            if (!currentObject) {
                // a path that does not resolve is a normal outcome (the AI probes
                // speculative paths and falls through to a property walk) — null is
                // what the signature promises; throwing made every probe an ERR
                this.logger.debug(`ThreePropertyReader: path ${address.propertyPath} does not resolve on ${obj} — segment ${segment} is undefined`);
                return null;
            }

            const isLastSegment = (i === segments.length - 1);
            if (isNodeMaterial(currentObject)) material = currentObject;
            if (isLastSegment) {
                const prop = this.createPropertyInternal(currentObject as Record<string, unknown>, segment, 0, []);
                if (prop) {
                    prop.metadata.address = buildAddress(obj, segments);
                    prop.metadata.addressOwner = object;
                    if (material && isNodeLike(currentObject) && !isUniformNode(currentObject)) {
                        const ownerMaterial = material;
                        prop.onDidChange(event => {
                            if (!event.transient && !event.hiddenOnly) forceNodeMaterialRecompile(ownerMaterial);
                        });
                    }
                }
                return prop;
            }

            // Component paths use the same host + guid identity as captured operations.
            if (segment === "components" && (currentObject as { isObject3D?: boolean }).isObject3D
                && segments[i + 1]?.startsWith("guid:")) {
                const components = (currentObject as { userData?: { components?: unknown[] } }).userData?.components ?? [];
                const component = resolveDocumentCollectionElement(components, segments[++i], {
                    report: message => this.logger.warn(`ThreePropertyReader: ${message}`),
                }) as object | undefined;
                if (!component) return null;
                currentObject = component;
                continue;
            }

            const isArrayIndex = segment.endsWith(']') && segment.includes('[');
            if (isArrayIndex) {
                const index = Number(segment.substring(segment.indexOf('[') + 1, segment.length - 1));
                currentObject = (currentObject as any)[index];
                continue;
            }
            currentObject = (currentObject as any)[segment];
        }

        this.logger.warn(`ThreePropertyReader: Property ${address.propertyPath} not found on object with ID ${obj}`);
        return null;
    }




}




namespace Internal {


    // #region Material Properties
    export function collectMaterialProperties(object: Object3D, prov: ThreePropertyReader, accessor: IPropertyAccessorService, state: IPropertyStateProvider, logger: ILogService): null | Array<IPropertyGroup | null> {

        if (isObjectWithMaterials(object)) {
            prov.discovery?.path(object, ["material"]);
            const mat = object.material;
            if (!mat) return null;

            if (Array.isArray(mat)) {
                const groups: IPropertyGroup[] = [];
                for (let i = 0; i < mat.length; i++) {
                    const material = mat[i];
                    prov.discovery?.member(mat, String(i), material);
                    if (!isMaterial(material)) continue;
                    const group = getMaterialProperties(object, material, prov, accessor, state, logger);
                    if (group) {
                        groups.push(group);
                    }
                }
                return groups;
            }
            else {
                if (!isMaterial(mat)) return null;
                const group = getMaterialProperties(object, mat, prov, accessor, state, logger);
                if (group) {
                    return [group];
                }
            }
        }

        return null;
    }

    /** `object` may be null when the material is inspected directly (no owning Object3D). */
    export function getMaterialProperties(object: Object3D | null, material: Material, prov: ThreePropertyReader, accessor: IPropertyAccessorService, state: IPropertyStateProvider, logger: ILogService): IPropertyGroup | null {

        // property addresses are object-scoped when an owner exists; a material
        // inspected on its own (Resources panel) is the address ROOT itself, so its
        // paths carry no `material` segment — `<uuid>/map`, not `<uuid>/material/map`
        const ownerId: string = (object ? handleOf(object) : null) ?? material.uuid;
        const pathPrefix: string[] = object ? ["material"] : [];

        prov.discovery?.watch(material);
        const keys = PropertyUtils.getAllObjectProperties(material);
        const group = new PropertyGroup("material", material.type || "Material");
        group.addCategory("materials");
        prov.decorations?.material(group, material);

        group.icon = "material";
        const isNodeMat = isNodeMaterial(material);

        for (const key in keys) {
            if (isNeedlePatchedBaseMaterialTextureSlot(material, key)) {
                continue;
            }

            if (state.isHidden(material, key)) {
                continue;
            }

            // Check if this property should be ignored (works for both NodeMaterials and regular materials with TSL nodes)
            if (isNodeMat && ignoreNodeMaterialProperty(material, key)) {
                continue;
            }

            // Also check for TSL node properties on non-NodeMaterials (e.g., MeshPhysicalMaterial with WebGPU)
            if (!isNodeMat && key.endsWith("Node")) {
                const value = (material as any)[key];
                if (isNodeLike(value)) {
                    continue;
                }
            }

            const desc = keys[key];
            const value = readPropertyDescriptorValue(material, key, desc);
            let type = desc.get ? typeof desc.get.call(material) : typeof value;

            if (type === "function") continue; // Skip functions

            if (key === "needsUpdate" && !desc.get) {
                type = "boolean";
            }

            const property = prov.createPropertyWithState(material, key, state);
            if (property) {
                property.metadata.address = buildAddress(String(ownerId), [...pathPrefix, key]);
                // the object the address is rooted at — `ownerId` is only its
                // session id, so consumers (changeset rows, attribution) would
                // otherwise have no way to say WHICH mesh this was edited on
                if (object) property.metadata.addressOwner = object;
                group.properties.push(property);

                addTextureProperties(object, material, key, property, key, prov, state);

                if (key === "uniforms") {
                    if (value && typeof value === "object") {
                        prov.discovery?.member(material, key, value);
                        prov.discovery?.watch(value);
                        for (const uniformName in value) {
                            const uniform = (value as Record<string, IUniform>)[uniformName];
                            prov.discovery?.member(value, uniformName, uniform);
                            if (uniform) prov.discovery?.watch(uniform);
                            const uniformValue = uniform ? uniform.value : null;
                            const metadata = getMetadata(uniform, "value", uniformValue);
                            if (isLikelyMaterialTextureInputSlot(uniformName, uniformValue)) {
                                metadata.accept = "null, image/*";
                            }
                            metadata.contextKey = `material.uniforms.${uniformName}`;
                            // A uniform is reached through the map it lives in. Without
                            // an address its edits are invisible to everything that
                            // works by address — save capture, undo, the AI tools —
                            // and its texture details had nothing to hang off.
                            metadata.address = buildAddress(String(ownerId), [...pathPrefix, "uniforms", uniformName, "value"]);
                            if (object) metadata.addressOwner = object;
                            const uniformGroupName = uniformGroupNameByUniform.get(uniformName);
                            if (uniformGroupName) {
                                metadata.detailsId = `details.${ownerId}.material.uniforms.${uniformGroupName}`;
                                metadata.detailsLabel = toUniformGroupLabel(uniformGroupName);
                            } else {
                                metadata.detailsId = `details.${ownerId}.material.uniforms`;
                                metadata.detailsLabel = "Uniforms";
                            }
                            const uniformProperty = new Property(uniform, "value", typeof uniformValue, metadata)
                            uniformProperty.label = uniformName;
                            postprocessThreeProperty(uniformProperty);
                            group.properties.push(uniformProperty);

                            addTextureProperties(object, uniform, "value", uniformProperty, uniformName, prov, state);

                            uniformProperty.onDidChange(() => {
                                logger.debug("Uniform changed:", uniformName, uniform.value, material.name);
                                material.needsUpdate = true;
                                if ("uniformsNeedUpdate" in material) {
                                    (material as any).uniformsNeedUpdate = true;
                                }
                                if ("needsUpdate" in uniform) {
                                    uniform.needsUpdate = true;
                                }
                            });
                        }
                    }
                }
            }
        }

        // Add TSL node properties for all materials (both NodeMaterials and regular materials with WebGPU TSL nodes)
        // Check if the material has any TSL node properties
        const tslProperties = extractTSLNodeProperties(material, material.type);
        if (tslProperties.length > 0) {

            // Group TSL properties by category for better organization
            const categorizedProps = new Map<string, typeof tslProperties>();
            for (const propInfo of tslProperties) {
                const category = propInfo.config.category;
                if (!categorizedProps.has(category)) {
                    categorizedProps.set(category, []);
                }
                categorizedProps.get(category)!.push(propInfo);
            }

            // Add TSL properties to the group
            for (const propInfo of tslProperties) {
                if (state.isHidden(material, propInfo.key)) {
                    continue;
                }

                prov.discovery?.member(material, propInfo.key, propInfo.node);
                const tslProperty = createTSLNodeProperty(material, { ...propInfo,
                    propertyPath: joinPath([...pathPrefix, propInfo.key]) }, ownerId, accessor, prov.discovery);
                tslProperty.metadata.addressOwner = object ?? material;
                group.properties.push(tslProperty);
            }
        }

        if (group.properties.length === 0) {
            return null;
        }
        return group;
    }

    const threeTextureFormats = [
        { label: "AlphaFormat", value: 1021 },
        { label: "RGBFormat", value: 1022 },
        { label: "RGBAFormat", value: 1023 },
        { label: "DepthFormat", value: 1026 },
        { label: "DepthStencilFormat", value: 1027 },
        { label: "RedFormat", value: 1028 },
        { label: "RedIntegerFormat", value: 1029 },
        { label: "RGFormat", value: 1030 },
        { label: "RGIntegerFormat", value: 1031 },
        { label: "RGBIntegerFormat", value: 1032 },
        { label: "RGBAIntegerFormat", value: 1033 },
        { label: "RGB_S3TC_DXT1_Format", value: 33776 },
        { label: "RGBA_S3TC_DXT1_Format", value: 33777 },
        { label: "RGBA_S3TC_DXT3_Format", value: 33778 },
        { label: "RGBA_S3TC_DXT5_Format", value: 33779 },
        { label: "RGB_PVRTC_4BPPV1_Format", value: 35840 },
        { label: "RGB_PVRTC_2BPPV1_Format", value: 35841 },
        { label: "RGBA_PVRTC_4BPPV1_Format", value: 35842 },
        { label: "RGBA_PVRTC_2BPPV1_Format", value: 35843 },
        { label: "RGB_ETC1_Format", value: 36196 },
        { label: "RGB_ETC2_Format", value: 37492 },
        { label: "RGBA_ETC2_EAC_Format", value: 37496 },
        { label: "R11_EAC_Format", value: 37488 },
        { label: "SIGNED_R11_EAC_Format", value: 37489 },
        { label: "RG11_EAC_Format", value: 37490 },
        { label: "SIGNED_RG11_EAC_Format", value: 37491 },
        { label: "RGBA_ASTC_4x4_Format", value: 37808 },
        { label: "RGBA_ASTC_5x4_Format", value: 37809 },
        { label: "RGBA_ASTC_5x5_Format", value: 37810 },
        { label: "RGBA_ASTC_6x5_Format", value: 37811 },
        { label: "RGBA_ASTC_6x6_Format", value: 37812 },
        { label: "RGBA_ASTC_8x5_Format", value: 37813 },
        { label: "RGBA_ASTC_8x6_Format", value: 37814 },
        { label: "RGBA_ASTC_8x8_Format", value: 37815 },
        { label: "RGBA_ASTC_10x5_Format", value: 37816 },
        { label: "RGBA_ASTC_10x6_Format", value: 37817 },
        { label: "RGBA_ASTC_10x8_Format", value: 37818 },
        { label: "RGBA_ASTC_10x10_Format", value: 37819 },
        { label: "RGBA_ASTC_12x10_Format", value: 37820 },
        { label: "RGBA_ASTC_12x12_Format", value: 37821 },
        { label: "RGBA_BPTC_Format", value: 36492 },
        { label: "RGB_BPTC_SIGNED_Format", value: 36494 },
        { label: "RGB_BPTC_UNSIGNED_Format", value: 36495 },
        { label: "RED_RGTC1_Format", value: 36283 },
        { label: "SIGNED_RED_RGTC1_Format", value: 36284 },
        { label: "RED_GREEN_RGTC2_Format", value: 36285 },
        { label: "SIGNED_RED_GREEN_RGTC2_Format", value: 36286 },
    ];
    const formatKeysWithoutAlphaChannel = [
        1022, 1026, 1028, 33776, 35840, 36196, 37492, 36494, 36283, 36284,
    ];
    const formatKeysWithAlphaChannel = threeTextureFormats.map(f => f.value).filter(v => !formatKeysWithoutAlphaChannel.includes(v));


    /**
     * Adds texture related sub-properties for a texture property (like format, width, height, colorSpace, mapping, channel, etc.)
     * @param object The Object3D the material belongs to (it might be a scene)
     * @param propertyOwner The material or object that owns the texture property (can be the same as object)
     * @param key The key of the texture property
     * @param property The texture property
     * @param propertyName The name of the texture property (for uniforms it's the uniform name)
     */
    export function addTextureProperties(object: Object3D | null, propertyOwner: Material | Object3D | IUniform, key: string, property: IProperty, propertyName: string, prov: ThreePropertyReader, state?: IPropertyStateProvider) {

        if (!property.metadata.contextKey) {
            return;
        }

        // The SLOT's own address is the root for its details. Composing
        // `<owner>/material/<key>` here instead said "the material of the material"
        // for a directly-inspected material, and `<scene>/material/background` for a
        // Scene, which has no material — none of those resolved.
        //
        // No address on the slot means its details get none either (the same rule
        // buildNestedAddress follows). The one slot in that position is a material
        // uniform: its key is the uniform's `value`, so a composed address would
        // collide across every uniform AND point at `material.value`, which does not
        // exist. "Not addressable" is the truth; a fabricated address is not.
        const base = property.metadata.address;
        /** address for one detail of this slot, or none when the slot has no address */
        const at = (suffix: string): string | undefined => base ? `${base}/${suffix}` : undefined;

        const value = (propertyOwner as any)[key];
        const slot: TextureSlot = { owner: propertyOwner, key };
        prov.discovery?.record(slot, slot);
        prov.discovery?.member(propertyOwner, key, value);

        // HACK: until we have a way to update the ContextKeyService dynamically, we skip color properties
        // maybe we can just pass in the contextkeyservice here?
        if (isColorLike(value)) {
            return;
        }

        const mayHaveTextureOrIsTexture = isTextureLike(value) || property.metadata.accept?.includes("image/*");

        if (!mayHaveTextureOrIsTexture) return;

        // TODO: we might need to expose this for uniforms
        const getTexture = () => getSlotTexture(slot);

        const allChildren: IProperty[] = [];

        const textureDetailsOwner = textureDetailsDefinition.bind(slot);
        const imageDetailsOwner = imageDetailsDefinition.bind(slot);


        function updateTexture(evt?: PropertyChangedEvent) {
            // hidden-toggle notifications from collection rebuilds are not
            // value changes — bumping the version for them invalidated the
            // preview blob cache and re-uploaded/re-PMREM'd environments on
            // EVERY inspector rebuild (up to +14 per rebuild across the
            // gated detail rows)
            if (evt?.hiddenOnly) return;
            const texture = getTexture() as Texture | null;
            if (isTextureLike(texture) || (typeof texture === "object" && texture !== null && "version" in texture)) {
                texture.version += 1;
            }
        }

        const OFFSET = 0.00001;
        let offsetCount = 0;

        // TODO: pass in registry to dynamically check if something is a color
        const when = typeof property.metadata.contextValue === "function"
            ? ContextKeyExpr.equals(property.metadata.contextKey, true)
            : ContextKeyExpr.notEquals(property.metadata.contextKey, null);

        // TODO: can we display texture type (e.g. DataTexture, etc.) as a string property?

        function addChild(child: IProperty) {
            // Every texture sub-row ("218.material.map.repeat") is reached THROUGH
            // the owning object, but the address only carries its session id.
            // Recorded once here rather than on each of the ~16 address literals —
            // this is the single funnel they all pass through. See
            // PropertyMetadata.addressOwner.
            if (object && child.metadata.address) child.metadata.addressOwner = object;
            postprocessThreeProperty(child);
            allChildren.push(child);
        }


        const formatContextKey = "material." + key + ".format";
        const formatProperty = new Property(textureDetailsOwner, "format", "number", {
            priority: property.metadata.priority !== undefined ? property.metadata.priority - (OFFSET * ++offsetCount) : undefined,
            address: at("format"),
            inset: true,
            when: when,
            contextKey: formatContextKey,
            description: "The format of the texture.",
            writeable: false,
            editable: false,
            static: true,
            enumOptions: threeTextureFormats,
        });
        formatProperty.label = "format";
        addChild(formatProperty);


        const widthProperty = new Property(imageDetailsOwner, "width", "number", {
            priority: property.metadata.priority !== undefined ? property.metadata.priority - (OFFSET * ++offsetCount) : undefined,
            address: at("image/width"),
            inset: true,
            when: when,
            description: "The width of the image.",
            writeable: false,
            editable: false,
            static: true,
        });
        widthProperty.label = "width";
        addChild(widthProperty);

        const heightProperty = new Property(imageDetailsOwner, "height", "number", {
            priority: property.metadata.priority !== undefined ? property.metadata.priority - (OFFSET * ++offsetCount) : undefined,
            address: at("image/height"),
            inset: true,
            when: when,
            description: "The height of the image.",
            writeable: false,
            editable: false,
            static: true,
        });
        heightProperty.label = "height";
        addChild(heightProperty);

        // ONE metadata source (no split): the central texture block (getMetadata)
        // is the BASE for these curated rows - value metadata (descriptions,
        // ranges, enum options, defaults) lives THERE and reaches the generic
        // "show more properties" rows identically. Inline fields here are
        // CURATION: order, addresses, visibility, context keys. The probe only
        // selects the metadata branch - texture metadata is per KEY, not per
        // instance, so a blank Texture answers the same as the live one.
        const metadataProbeTexture = new Texture();
        const textureMeta = (metaKey: string, curation: PropertyMetadata): PropertyMetadata => {
            const probe = (getTexture() ?? metadataProbeTexture) as unknown as Record<string, unknown>;
            return { ...getMetadata(probe as object, metaKey, probe[metaKey]), ...curation };
        };

        const colorSpaceProperty = new Property(textureDetailsOwner, "colorSpace", "string", textureMeta("colorSpace", {
            priority: property.metadata.priority !== undefined ? property.metadata.priority - (OFFSET * ++offsetCount) : undefined,
            address: at("colorSpace"),
            inset: true,
            when: when,
        }));
        colorSpaceProperty.onDidChange((evt) => {
            updateTexture(evt);
        });
        colorSpaceProperty.label = "colorSpace";
        addChild(colorSpaceProperty);

        const uvChannelProperty = new Property(textureDetailsOwner, "channel", "number", textureMeta("channel", {
            priority: property.metadata.priority !== undefined ? property.metadata.priority - (OFFSET * ++offsetCount) : undefined,
            address: at("channel"),
            inset: true,
            when: when,
        }));
        // TODO: not sure if we need to update texture on channel change? let's just do it to be safe
        uvChannelProperty.onDidChange((evt) => {
            updateTexture(evt);
        });
        uvChannelProperty.label = "channel";
        addChild(uvChannelProperty);

        const mappingContextKey = "material." + key + ".mapping";
        const mappingProperty = new Property(textureDetailsOwner, "mapping", "number", textureMeta("mapping", {
            priority: property.metadata.priority !== undefined ? property.metadata.priority - (OFFSET * ++offsetCount) : undefined,
            address: at("mapping"),
            inset: true,
            when: when,
            contextKey: mappingContextKey,
        }));
        mappingProperty.onDidChange((evt) => {
            updateTexture(evt);
        });
        mappingProperty.label = "mapping";
        addChild(mappingProperty);

        const flipyProperty = new Property(textureDetailsOwner, "flipY", "boolean", textureMeta("flipY", {
            priority: property.metadata.priority !== undefined ? property.metadata.priority - (OFFSET * ++offsetCount) : undefined,
            address: at("flipY"),
            inset: true,
            when: when,
        }));
        flipyProperty.onDidChange((evt) => {
            updateTexture(evt);
        });
        flipyProperty.label = "flipY";
        addChild(flipyProperty);

        const isNotEquirectMapping = ContextKeyExpr.and(
            ContextKeyExpr.notEquals(mappingContextKey, 303), // not EquirectangularReflectionMapping
            ContextKeyExpr.notEquals(mappingContextKey, 304), // not EquirectangularRefractionMapping
            ContextKeyExpr.notEquals(mappingContextKey, 306) // not CubeUVReflectionMapping
        );

        const repeatProperty = new Property(textureDetailsOwner, "repeat", "vector2", textureMeta("repeat", {
            priority: property.metadata.priority !== undefined ? property.metadata.priority - (OFFSET * ++offsetCount) : undefined,
            address: at("repeat"),
            inset: true,
            when: ContextKeyExpr.and(
                when,
                isNotEquirectMapping
            ),
        }));
        repeatProperty.label = "repeat";
        addChild(repeatProperty);

        const offsetProperty = new Property(textureDetailsOwner, "offset", "vector2", textureMeta("offset", {
            priority: property.metadata.priority !== undefined ? property.metadata.priority - (OFFSET * ++offsetCount) : undefined,
            address: at("offset"),
            inset: true,
            when: ContextKeyExpr.and(
                when,
                isNotEquirectMapping
            ),
        }));
        offsetProperty.label = "offset";
        addChild(offsetProperty);

        const clampSProperty = new Property(textureDetailsOwner, "wrapS", "number", textureMeta("wrapS", {
            priority: property.metadata.priority !== undefined ? property.metadata.priority - (OFFSET * ++offsetCount) : undefined,
            address: at("wrapS"),
            inset: true,
            when: ContextKeyExpr.and(
                when,
                isNotEquirectMapping
            ),
        }));
        clampSProperty.onDidChange((evt) => {
            updateTexture(evt);
        });
        clampSProperty.label = "wrapS (U)";
        addChild(clampSProperty);

        const clampTProperty = new Property(textureDetailsOwner, "wrapT", "number", textureMeta("wrapT", {
            priority: property.metadata.priority !== undefined ? property.metadata.priority - (OFFSET * ++offsetCount) : undefined,
            address: at("wrapT"),
            inset: true,
            when: ContextKeyExpr.and(
                when,
                isNotEquirectMapping
            ),
        }));
        clampTProperty.onDidChange((evt) => {
            updateTexture(evt);
        });
        clampTProperty.label = "wrapT (V)";
        addChild(clampTProperty);


        const rotationProperty = new Property(textureDetailsOwner, "rotation", "number", textureMeta("rotation", {
            priority: property.metadata.priority !== undefined ? property.metadata.priority - (OFFSET * ++offsetCount) : undefined,
            address: at("rotation"),
            inset: true,
            when: ContextKeyExpr.and(
                when,
                isNotEquirectMapping
            ),
        }));
        rotationProperty.label = "rotation";
        addChild(rotationProperty);

        // TODO: we can hide center if repeat is (1,1) and offset is (0,0) and rotation is 0? (not sure if that's useful though)
        const centerProperty = new Property(textureDetailsOwner, "center", "vector2", textureMeta("center", {
            priority: property.metadata.priority !== undefined ? property.metadata.priority - (OFFSET * ++offsetCount) : undefined,
            address: at("center"),
            inset: true,
            when: ContextKeyExpr.and(
                when,
                isNotEquirectMapping
            ),
        }));
        centerProperty.label = "center";
        addChild(centerProperty);

        const premultiplyAlphaProperty = new Property(textureDetailsOwner, "premultiplyAlpha", "boolean", textureMeta("premultiplyAlpha", {
            priority: property.metadata.priority !== undefined ? property.metadata.priority - (OFFSET * ++offsetCount) : undefined,
            address: at("premultiplyAlpha"),
            inset: true,
            when: ContextKeyExpr.and(
                when,
                // TODO: we need to have a dynamic context key expression (or a way to trigger an context key update for the format when a texture is assigned - otherwise the context key expression is static and won't update when a texture is assigned from NULL to something (because initially f.value is null))
                ContextKeyExpr.or(
                    ...formatKeysWithAlphaChannel.map(fk => ContextKeyExpr.equals(formatContextKey, fk))
                )
            ),
        }));
        premultiplyAlphaProperty.label = "premultiplyAlpha";
        addChild(premultiplyAlphaProperty);



        const anistropyProperty = new Property(textureDetailsOwner, "anisotropy", "number", textureMeta("anisotropy", {
            priority: property.metadata.priority !== undefined ? property.metadata.priority - (OFFSET * ++offsetCount) : undefined,
            address: at("anisotropy"),
            inset: true,
            when: when,
        }));
        anistropyProperty.onDidChange((evt) => {
            updateTexture(evt);
        });
        anistropyProperty.label = "anisotropy";
        addChild(anistropyProperty);

        const moreContainer = textureMoreDefinition.bind(slot);

        const moreProperty = new Property(moreContainer, "value", "object", {
            priority: property.metadata.priority !== undefined ? property.metadata.priority - (OFFSET * ++offsetCount) : undefined,
            address: at("more"),
            inset: true,
            when,
            editable: false,
            writeable: false,
            readable: true,
            static: true,
        });
        moreProperty.label = "More";
        Object.defineProperty(moreProperty, "children", {
            configurable: true,
            enumerable: true,
            get() {
                const currentTexture = getTexture();
                if (!currentTexture) return [];
                prov.discovery?.member(propertyOwner, key, currentTexture);
                return prov.createChildPropertiesForObject(
                    currentTexture,
                    base,
                    0,
                    [],
                    state,
                );
            },
        });
        moreProperty.metadata.childrenExpanded = false;
        addChild(moreProperty);

        const syncTextureChildrenVisibility = () => {
            const texture = getTexture();
            const hasTexture = isTextureLike(texture);
            property.children = hasTexture ? allChildren : undefined;
            property.metadata.childrenLabel = hasTexture ? `${propertyName} details` : undefined;
            if (hasTexture && property.metadata.childrenExpanded === undefined) {
                property.metadata.childrenExpanded = false;
            }
            if (!hasTexture) {
                property.metadata.childrenExpanded = false;
            }
        };

        syncTextureChildrenVisibility();
        property.onDidChange(() => {
            syncTextureChildrenVisibility();
        });
    }

}



// #region Needle

export namespace Needle {

    export function objectHasComponents(thing: unknown): { hasComponents: boolean; types: Array<"gltf" | "runtime"> } {
        const result = { hasComponents: false, types: [] as Array<"gltf" | "runtime"> };

        if (typeof thing === "object" && thing !== null) {
            const object = thing as Record<string, any>;
            if (object.userData?.components && Array.isArray(object.userData.components) && object.userData.components.length > 0) {
                result.hasComponents = true;
                result.types.push("runtime");
            }
            // `userData?.` — this takes `unknown` and is called with materials,
            // plain data objects and anything else the inspector drills into, none
            // of which have userData. The runtime branch above already guards it.
            if (object.userData?.gltfExtensions?.NEEDLE_components?.builtin_components?.length) {
                result.hasComponents = true;
                result.types.push("gltf");
            }
        }

        return result;
    }

    export function getNeedleComponents(thing: unknown): { runtimeComponents: Array<MaybeNeedleRuntimeComponent>; gltfComponents: Array<NeedleGltfComponent> } {
        const result = { runtimeComponents: [] as Array<MaybeNeedleRuntimeComponent>, gltfComponents: [] as Array<NeedleGltfComponent> };

        if (typeof thing === "object" && thing !== null) {
            const object = thing as Record<string, any>;
            if (object.userData?.components && Array.isArray(object.userData.components)) {
                for (const comp of object.userData.components) {
                    if (isNeedleRuntimeComponent(comp)) {
                        result.runtimeComponents.push(comp);
                    }
                }
            }
            if (object.userData?.gltfExtensions?.NEEDLE_components?.builtin_components && Array.isArray(object.userData.gltfExtensions.NEEDLE_components.builtin_components)) {
                for (const comp of object.userData.gltfExtensions.NEEDLE_components.builtin_components) {
                    if (isNeedleGltfComponent(comp)) {
                        result.gltfComponents.push(comp);
                    }
                }
            }
        }
        return result;
    }

    export type MaybeNeedleRuntimeComponent = Record<string | symbol, any> & {
        "__name"?: string;
        "$serializedTypes"?: Record<string, any>;
        "needle-component-name"?: string;
    }
    export function isNeedleRuntimeComponent(obj: any): obj is MaybeNeedleRuntimeComponent {
        return obj && typeof obj === "object" && obj.$serializedTypes && typeof obj.$serializedTypes === "object";
    }

    type VolumeParameter = { value: unknown };
    export function isNeedleVolumeParameter(obj: any): obj is VolumeParameter {
        return obj && typeof obj === "object" && "isVolumeParameter" in obj && obj.isVolumeParameter === true;
    }

    export type NeedleGltfComponent = {
        name: string,
        guid: string,
        enabled: boolean
    } & Record<string, any>;

    export function isNeedleGltfComponent(obj: any): obj is NeedleGltfComponent {
        return obj && typeof obj === "object" && typeof obj.name === "string" && typeof obj.guid === "string" && typeof obj.enabled === "boolean" && obj.gameObject === undefined; // to distinguish from runtime components which might also have name, guid and enabled properties
    }

    export function collectComponentProperties(type: "gltf" | "runtime", obj: MaybeNeedleRuntimeComponent | NeedleGltfComponent, prov: ThreePropertyReader, group: IPropertyGroup, state: IPropertyStateProvider, logger: ILogService): void {

        const name = resolveComponentName(obj);
        if (name) {
            group.name = name;
            const docsUrl = `https://engine.needle.tools/docs/api/${group.name}`;
            prov.decorations?.docs(group, docsUrl);
            const typeInfo = NeedleMeta.getNeedleTypeMeta(group.name);
            group.description = NeedleMeta.getTypeDescriptionShort(group.name) || undefined;
            group.tooltip = typeInfo?.comment?.text || typeInfo?.comment?.shortText;
        }
        else {
            logger.warn("Needle Component: Could not resolve component name", obj);
        }

        prov.discovery?.path(obj, ["$serializedTypes"]);
        if (isNeedleRuntimeComponent(obj)) {
            // MERGE the whole prototype chain: @serializable gives every class its
            // OWN $serializedTypes map (no chaining — deliberate, so same-name
            // members with different types don't clobber), so `obj.$serializedTypes`
            // alone is only the most-derived class and inherited members
            // (Collider.isTrigger on a BoxCollider) never became rows. Most-derived
            // wins on shadowed names.
            const types: Record<string, unknown> = {};
            for (let proto = obj as object | null; proto; proto = Object.getPrototypeOf(proto)) {
                const own = Object.getOwnPropertyDescriptor(proto, "$serializedTypes")?.value as Record<string, unknown> | undefined;
                if (own) {
                    prov.discovery?.member(proto, "$serializedTypes", own);
                    prov.discovery?.watch(own);
                    for (const key of Object.keys(own)) types[key] ??= own[key];
                }
                prov.discovery?.prototype(proto, Object.getPrototypeOf(proto));
            }
            for (const key in types) {
                if (state.isHidden(obj, key)) {
                    continue;
                }
                    const property = prov.createPropertyWithState(obj, key, state);
                if (property) {
                    postprocessComponentProperty(property, group.name);
                    group.properties.push(property);
                }
            }

        }
        else if (type === "gltf") {
            const keys = Object.keys(obj);
            for (const key of keys) {
                if (key === "name") continue; // skip name property for glTF components
                if (state.isHidden(obj, key)) {
                    continue;
                }
                // ignore symbols
                const property = prov.createPropertyWithState(obj, key, state);
                if (property) {
                    postprocessComponentProperty(property, group.name);
                    group.properties.push(property);
                }
            }
        }
    }


    function postprocessComponentProperty(property: IProperty, componentName?: string) {
        switch (property.key) {
            case "sourceId":
            case "guid":
            case "needle_editor_guid":
                property.metadata.editable = false;
                break;
        }
        // RICH ROWS from the engine's own metadata (components.needle.json —
        // injected as the needle types info): an enum-typed member renders as a
        // dropdown of the enum NAMES, bitmask enums multi-select, and the member's
        // doc comment becomes the row description. Generic — one lookup point for
        // every component; nothing per-component.
        if (componentName) {
            if (!property.metadata.enumOptions) {
                const enumInfo = NeedleMeta.getEnumOptions(componentName, property.key);
                if (enumInfo) {
                    property.metadata.enumOptions = enumInfo.options;
                    if (enumInfo.multiple) property.metadata.multiple = true;
                }
            }
            if (!property.metadata.description) {
                property.metadata.description = NeedleMeta.getPropertyDescription(componentName, property.key) ?? undefined;
            }
        }
    }
}
